import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Form, GetProp, Input, Row, Upload, UploadProps } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { FormProps } from 'antd/es/form/Form';

// services
import NotificationService from 'core/services/notification';
import { onCreateBank } from 'modules/banks/services/banks';

// components
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	logo: any;
};

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function CreateBankPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [createForm] = Form.useForm();
	const [imageUrl, setImageUrl] = useState<string>();

	const inputStyles = {
		width: 500,
		maxWidth: '100%',
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createBankPayload = new FormData();
			createBankPayload.append('name[ar]', values['name[ar]']);
			createBankPayload.append('name[en]', values['name[en]']);
			createBankPayload.append('active', '1');
			createBankPayload.append('logo', values.logo);

			await onCreateBank(createBankPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('BANKS.CREATED_SUCCESS'));
			navigate('/banks');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('BANKS.CREATE_ERROR'));
		}
	};

	const beforeUpload = (file: FileType) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
		if (!isJpgOrPng) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ONLY_PNG_JPG'));
			return false;
		}

		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(reader.result as string));
		});

		return false;
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		createForm.setFieldsValue({ logo: info.file });
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('BANKS.CREATE_TITLE')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('BANKS.DETAILS')}</h5>

				<Form name="createForm" form={createForm} onFinish={onSubmit}>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<h6>{t('BANKS.INFORMATION')}</h6>
							<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('BANKS.VALIDATION.NAME_EN') }]}>
								<Input size="large" placeholder={t('BANKS.PLACEHOLDER.NAME_EN')} style={inputStyles} />
							</Form.Item>

							<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('BANKS.VALIDATION.NAME_AR') }]}>
								<Input size="large" placeholder={t('BANKS.PLACEHOLDER.NAME_AR')} style={inputStyles} />
							</Form.Item>
						</Col>

						<Col span={24} md={12}>
							<div>
								<h6>{t('BANKS.LOGO')}</h6>
								<Form.Item<FieldType> name="logo" hidden>
									<Input />
								</Form.Item>
								<Upload
									className="squareInput"
									listType="picture-card"
									showUploadList={false}
									beforeUpload={beforeUpload}
									onChange={handleChange}>
									{imageUrl ? (
										<img src={imageUrl} alt="avatar" style={{ width: '100%' }} />
									) : (
										<button style={{ border: 0, background: 'none' }} type="button">
											{isSubmitting ? <LoadingOutlined /> : <PlusOutlined />}
											<div style={{ marginTop: 8 }}>{t('COMMON.UPLOAD')}</div>
										</button>
									)}
								</Upload>
							</div>
						</Col>
					</Row>
				</Form>

				<Form.Item>
					<Button
						size="middle"
						type="primary"
						htmlType="submit"
						form="createForm"
						loading={isSubmitting}
						style={{ marginTop: 20, display: 'flex' }}>
						{t('COMMON.ADD')}
					</Button>
				</Form.Item>
			</div>
		</>
	);
}

export default CreateBankPage;
